/* =========================================================
   custom-hero-slider.js
   Progress bar uses a native CSS transition (width 0% → 100%
   over autoplay duration). No rAF bookkeeping.
   ========================================================= */

class CustomHeroSlider {
  constructor(el) {
    this.el = el;
    this.slides = Array.from(el.querySelectorAll('.custom-hero-slider__slide'));
    this.total = this.slides.length;
    if (this.total < 1) return;

    this.counterEl = el.querySelector('.custom-hero-slider__counter-current');
    this.progressEl = el.querySelector('.custom-hero-slider__progress-bar');
    this.prevBtn = el.querySelector('.custom-hero-slider__arrow--prev');
    this.nextBtn = el.querySelector('.custom-hero-slider__arrow--next');

    this.index = 0;
    this.autoplay = el.dataset.autoplay === 'true';
    this.speedMs = Math.max(1000, parseInt(el.dataset.autoplaySpeed, 10) || 5000);
    this.pauseOnHover = el.dataset.pauseOnHover === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timer = null;

    this.bindEvents();
    this.render(0);
    if (this.autoplay && this.total > 1 && !this.reducedMotion) this.start();
  }

  bindEvents() {
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.go(this.index + 1, true));
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.go(this.index - 1, true));

    if (this.pauseOnHover) {
      this.el.addEventListener('mouseenter', () => this.stop());
      this.el.addEventListener('mouseleave', () => this.start());
      this.el.addEventListener('focusin', () => this.stop());
      this.el.addEventListener('focusout', () => this.start());
    }

    this.onKey = (e) => {
      if (!this.isVisible()) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1, true); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1, true); }
    };
    window.addEventListener('keydown', this.onKey);

    this.onVisibility = () => (document.hidden ? this.stop() : this.start());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onBlockSelect = (e) => {
      if (!this.el.contains(e.target)) return;
      const idx = this.slides.indexOf(e.target);
      if (idx >= 0) { this.stop(); this.go(idx, false); }
    };
    this.onBlockDeselect = (e) => {
      if (!this.el.contains(e.target)) return;
      if (this.autoplay && !this.reducedMotion) this.start();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  destroy() {
    this.stop();
    window.removeEventListener('keydown', this.onKey);
    document.removeEventListener('visibilitychange', this.onVisibility);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
  }

  isVisible() {
    const r = this.el.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight;
  }

  go(target, userInitiated) {
    this.index = ((target % this.total) + this.total) % this.total;
    this.render(this.index);
    if (userInitiated && this.autoplay && !this.reducedMotion) this.start();
  }

  render(next) {
    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      const v = slide.querySelector('video');
      if (v) active ? v.play().catch(() => {}) : v.pause();
    });
    if (this.counterEl) this.counterEl.textContent = String(next + 1);
    this.setProgress(next);
  }

  /* Progress shows position in the deck: (index + 1) / total. */
  setProgress(index) {
    const bar = this.progressEl;
    if (!bar) return;
    const fraction = this.total > 0 ? (index + 1) / this.total : 0;
    bar.style.transform = `scaleX(${fraction})`;
  }

  start() {
    if (!this.autoplay || this.total < 2 || this.reducedMotion) return;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.go(this.index + 1, false), this.speedMs);
  }

  stop() {
    clearTimeout(this.timer);
    this.timer = null;
  }
}

/* ---- Initializer ---- */
function initCustomHeroSliders(root) {
  const scope = root || document;
  scope.querySelectorAll('.custom-hero-slider').forEach((el) => {
    if (!el.__customHeroSlider) {
      el.__customHeroSlider = new CustomHeroSlider(el);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initCustomHeroSliders());
} else {
  initCustomHeroSliders();
}

/* Shopify theme editor — re-init on section load, tear down on unload */
document.addEventListener('shopify:section:load', (e) => initCustomHeroSliders(e.target));
document.addEventListener('shopify:section:unload', (e) => {
  e.target.querySelectorAll('.custom-hero-slider').forEach((el) => {
    if (el.__customHeroSlider) {
      el.__customHeroSlider.destroy();
      delete el.__customHeroSlider;
    }
  });
});
